/**
 * Jobs Map View
 *
 * Map view showing job locations and routes by driver.
 * PURELY PRESENTATIONAL - uses hooks for operations.
 *
 * DDD Compliance:
 * - Uses JobCardViewModel from application layer
 * - Direct Mapbox dependency is fine (presentation layer)
 * - Delegates operations to use case hooks
 * - No route optimization (just visualizes scheduled order)
 *
 * Requires VITE_MAPBOX_ACCESS_TOKEN (public token: pk.*)
 */

import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';
import { geocodeAddress, type Coordinates } from '@/lib/geocoding';
import { groupJobsByDriver } from '@/lib/routeUtils';
import 'mapbox-gl/dist/mapbox-gl.css';

// Mapbox access token from environment
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface JobsMapViewProps {
  jobs: JobCardViewModel[];
  onViewDetails: (jobId: string) => void;
}

/**
 * Get icon for service type
 * Returns an emoji/unicode icon that clearly indicates the service type
 */
function getServiceTypeIcon(serviceType: string): string {
  switch (serviceType.toLowerCase()) {
    case 'delivery':
      return '⬇️'; // Down arrow - delivering container to site
    case 'pickup':
      return '⬆️'; // Up arrow - picking up container from site
    case 'swap':
      return '🔄'; // Swap arrows - exchanging containers
    case 'permanent_placement':
      return '📍'; // Pin - permanent placement
    default:
      return '📦'; // Box - generic
  }
}

export function JobsMapView({ jobs, onViewDetails }: JobsMapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [geocodedCoordinates, setGeocodedCoordinates] = useState<
    Record<string, Coordinates>
  >({});
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // Initialize map
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      console.warn('Mapbox access token not configured');
      return;
    }

    if (!mapContainer.current) return;
    if (map.current) return; // Already initialized

    try {
      mapboxgl.accessToken = MAPBOX_TOKEN;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-119.4179, 36.7783], // Default: California
        zoom: 8,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Handle map load errors
      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setMapError('Failed to load map. Please try refreshing the page.');
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError(
        'Map initialization failed. This may be due to browser security settings. ' +
        'Please ensure you are using HTTPS or localhost, and that WebGL is enabled in your browser.'
      );
    }

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Geocode job addresses that don't have coordinates
  useEffect(() => {
    if (!jobs.length) return;

    const geocodeJobs = async () => {
      setIsGeocoding(true);
      const newCoordinates: Record<string, Coordinates> = { ...geocodedCoordinates };
      let hasNewCoordinates = false;

      for (const job of jobs) {
        // Skip if already geocoded
        if (newCoordinates[job.id]) continue;

        // Skip if no address
        if (!job.displayAddress) continue;

        // Geocode the address
        const coords = await geocodeAddress(job.displayAddress);
        if (coords) {
          newCoordinates[job.id] = coords;
          hasNewCoordinates = true;
        }
      }

      if (hasNewCoordinates) {
        setGeocodedCoordinates(newCoordinates);
      }
      setIsGeocoding(false);
    };

    geocodeJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  // Render markers and routes on map
  useEffect(() => {
    if (!map.current) return;
    if (!jobs.length) return;

    try {
      // Clear existing markers and layers
      const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
      existingMarkers.forEach((marker) => marker.remove());

      // Remove existing route layers
      if (map.current.getLayer('routes')) {
        map.current.removeLayer('routes');
      }
      if (map.current.getSource('routes')) {
        map.current.removeSource('routes');
      }

      // Group jobs by driver
      const routes = groupJobsByDriver(jobs);

      // Track bounds for auto-fitting
      const bounds = new mapboxgl.LngLatBounds();
      let hasMarkers = false;

      // Add markers for each job
      jobs.forEach((job) => {
        const coords = geocodedCoordinates[job.id];
        if (!coords) return; // Skip jobs without coordinates

        // Get service type icon and label
        const serviceIcon = getServiceTypeIcon(job.serviceType);
        const statusColor = job.statusBadge.color === 'blue' ? '#3B82F6' :
                           job.statusBadge.color === 'yellow' ? '#F59E0B' :
                           job.statusBadge.color === 'green' ? '#10B981' :
                           job.statusBadge.color === 'red' ? '#EF4444' : '#6B7280';

        // Create marker element with icon and container size
        const el = document.createElement('div');
        el.className = 'job-marker';
        el.style.cssText = `
          background: white;
          border: 3px solid ${statusColor};
          border-radius: 8px;
          padding: 4px 6px;
          cursor: pointer;
          box-shadow: 0 3px 8px rgba(0,0,0,0.3);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          min-width: 42px;
          font-family: system-ui, -apple-system, sans-serif;
        `;
        
        el.innerHTML = `
          <div style="font-size: 16px; line-height: 1;">${serviceIcon}</div>
          <div style="font-size: 10px; font-weight: 600; color: #374151; line-height: 1;">${job.containerSize}</div>
        `;

        // Create popup with enhanced service info
        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div style="padding: 8px; min-width: 200px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              <span style="font-size: 20px;">${serviceIcon}</span>
              <div>
                <h3 style="margin: 0; font-weight: 600; font-size: 14px;">${job.displayName}</h3>
                <p style="margin: 0; font-size: 11px; color: #6B7280;">
                  ${job.serviceType} • ${job.containerSize}
                </p>
              </div>
            </div>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
              ${job.displayAddress || 'No address'}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 12px;">
              Status: <span style="color: ${statusColor}; font-weight: 500;">
                ${job.statusBadge.label}
              </span>
            </p>
            ${job.assignedDriver ? `
              <p style="margin: 0; font-size: 12px;">
                Driver: <strong>${job.assignedDriver.name}</strong>
              </p>
            ` : ''}
          </div>
        `);

        // Add marker to map
        new mapboxgl.Marker(el)
          .setLngLat([coords.longitude, coords.latitude])
          .setPopup(popup)
          .addTo(map.current!);

        // Click handler
        el.addEventListener('click', () => {
          onViewDetails(job.id);
        });

        bounds.extend([coords.longitude, coords.latitude]);
        hasMarkers = true;
      });

      // Draw route lines for each driver
      const routeFeatures = routes
        .map((route) => {
          const coordinates = route.jobs
            .map((job) => geocodedCoordinates[job.id])
            .filter((coords): coords is Coordinates => !!coords)
            .map((coords) => [coords.longitude, coords.latitude]);

          if (coordinates.length < 2) return null;

          return {
            type: 'Feature' as const,
            properties: {
              color: route.color,
              driverName: route.driverName,
            },
            geometry: {
              type: 'LineString' as const,
              coordinates,
            },
          };
        })
        .filter((feature): feature is NonNullable<typeof feature> => !!feature);

      if (routeFeatures.length > 0) {
        map.current.addSource('routes', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: routeFeatures,
          },
        });

        map.current.addLayer({
          id: 'routes',
          type: 'line',
          source: 'routes',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 3,
            'line-opacity': 0.7,
          },
        });
      }

      // Fit bounds to show all markers
      if (hasMarkers) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
        });
      }
    } catch (error) {
      console.error('Failed to render map markers/routes:', error);
      setMapError(
        'Map rendering failed due to browser security restrictions. ' +
        'This typically happens when WebGL is disabled or localStorage access is blocked. ' +
        'Please check your browser settings or try a different browser.'
      );
    }
  }, [jobs, geocodedCoordinates, onViewDetails]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="text-center py-12">
            <p className="text-neutral-600">
              Map view requires Mapbox access token to be configured.
            </p>
            <p className="text-sm text-neutral-500 mt-2">
              Please set VITE_MAPBOX_ACCESS_TOKEN environment variable.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="text-center py-12">
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Map Error</h3>
            <p className="text-neutral-600 max-w-md mx-auto">{mapError}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 btn-primary"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-content" style={{ padding: 0, position: 'relative' }}>
        {isGeocoding && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 1,
              backgroundColor: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '14px',
            }}
          >
            Geocoding addresses...
          </div>
        )}
        <div ref={mapContainer} style={{ width: '100%', height: '700px' }} />
      </div>
    </div>
  );
}
